import { useEffect, useState } from "react";
import { Alert, Field, SelectField, Stack } from "@leylion/design-system";
import { searchCrmCustomers } from "../api/client";
import type { CustomerListItem } from "../api/crm-types";
import { valueError } from "../shared/value-copy";

export type GiftCardRecipient = { kind: "none" | "registered" | "guest"; customer: CustomerListItem | null; name: string; contact: string };
export const emptyGiftCardRecipient: GiftCardRecipient = { kind: "none", customer: null, name: "", contact: "" };

export function GiftCardRecipientFields({ language, value, onChange, canReadCustomers, disabled }: {
  language: "ar" | "en"; value: GiftCardRecipient; onChange: (value: GiftCardRecipient) => void; canReadCustomers: boolean; disabled: boolean;
}) {
  const ar = language === "ar";
  const [query, setQuery] = useState(""); const [results, setResults] = useState<CustomerListItem[]>([]);
  const [loading, setLoading] = useState(false); const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    setResults([]); setError(""); setLoading(false);
    if (disabled || !canReadCustomers || value.kind !== "registered" || !query.trim()) return;
    setLoading(true);
    const timer = window.setTimeout(() => {
      // Same bounded CRM search as customer-value accounts; no customer creation.
      void searchCrmCustomers(query.trim(), undefined, undefined, undefined, false, 25)
        .then(items => { if (!cancelled) setResults(items); })
        .catch(caught => { if (!cancelled) setError(valueError(caught, language)); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 250);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [query, value.kind, disabled, canReadCustomers, language]);
  const options = value.customer && !results.some(item => item.id === value.customer?.id) ? [value.customer, ...results] : results;
  return <Stack>
    <SelectField data-testid="gift-recipient-kind" label={ar ? "المستلم" : "Recipient"} value={value.kind} disabled={disabled} onChange={event => {
      setQuery(""); setResults([]); onChange({ ...emptyGiftCardRecipient, kind: event.target.value as GiftCardRecipient["kind"] });
    }}><option value="none">{ar ? "بدون بيانات مستلم" : "No recipient details"}</option>
      {canReadCustomers ? <option value="registered">{ar ? "عميل مسجل" : "Registered customer"}</option> : null}
      <option value="guest">{ar ? "مستلم غير مسجل" : "Unregistered recipient"}</option>
    </SelectField>
    {value.kind === "registered" && canReadCustomers ? <Stack>
      <Field data-testid="gift-recipient-search" label={ar ? "البحث عن عميل" : "Search customers"} placeholder={ar ? "الاسم أو الهاتف أو رقم العميل" : "Name, phone or customer number"} value={query} disabled={disabled} autoComplete="off" onChange={event => setQuery(event.target.value)} />
      <SelectField data-testid="gift-issue-customer" label={ar ? "العميل" : "Customer"} value={value.customer?.id ?? ""} disabled={disabled} onChange={event => onChange({ ...emptyGiftCardRecipient, kind: "registered", customer: options.find(item => item.id === event.target.value) ?? null })}>
        <option value="">{ar ? "اختر العميل" : "Select customer"}</option>
        {options.map(item => <option key={item.id} value={item.id}>{item.name} · {item.phone || item.customerNumber} · {item.customerNumber}</option>)}
      </SelectField>
      {loading ? <small role="status">{ar ? "جارٍ البحث…" : "Searching…"}</small> : null}
      {error ? <Alert tone="danger" title={ar ? "تعذر البحث" : "Search failed"}>{error}</Alert> : null}
      {!loading && !error && query.trim() && !results.length ? <small>{ar ? "لا توجد نتائج." : "No results."}</small> : null}
    </Stack> : null}
    {value.kind === "guest" ? <Stack>
      <Field data-testid="gift-recipient-name" label={ar ? "اسم المستلم (اختياري)" : "Recipient name (optional)"} maxLength={220} value={value.name} disabled={disabled} onChange={event => onChange({ ...value, name: event.target.value })} />
      <Field data-testid="gift-recipient-contact" label={ar ? "وسيلة التواصل (اختياري)" : "Contact details (optional)"} maxLength={240} value={value.contact} disabled={disabled} onChange={event => onChange({ ...value, contact: event.target.value })} />
    </Stack> : null}
  </Stack>;
}
